import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import Modal from './Common/Modal';
import { useAuth } from '../contexts/AuthContext';

export default function ProfileModal({ isOpen, onClose, user, onUpdate }) {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';

  // Only editable fields live in form state.
  // Email and CNIC are managed identity → read-only for the user.
  const [form, setForm] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || '',
    address: user?.address || '',
  });

  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: '',
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  // Re-sync form state whenever the modal opens or the user object changes.
  // Prevents stale values from a previous session showing up.
  useEffect(() => {
    if (isOpen && user) {
      setForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        address: user.address || '',
      });
      setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
      setError('');
      setSuccess('');
    }
  }, [isOpen, user]);

  // ─── Update profile ─────────────────────────────────────────
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Send only the fields the backend accepts for self-service.
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim() || null,
        address: form.address.trim() || null,
      };

      await api.put('/profile/me', payload);
      setSuccess('✅ Profile updated successfully!');
      onUpdate?.();
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  // ─── Change password ────────────────────────────────────────
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (passwordData.new_password !== passwordData.confirm_password) {
      setError('New password and confirmation do not match.');
      return;
    }
    if (passwordData.new_password.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }

    setLoading(true);
    try {
      await api.post('/profile/change-password', {
        old_password: passwordData.old_password,
        new_password: passwordData.new_password,
      });
      setSuccess('✅ Password changed successfully!');
      setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Password change failed');
    } finally {
      setLoading(false);
    }
  };

  // ─── Download DB (admin only) ───────────────────────────────
  const handleDownloadDB = async () => {
    if (window.confirm('📥 Download the entire database backup?')) {
      // Use fetch with the auth header — window.location.href won't send the token
      try {
        const token = localStorage.getItem('token');
        const res = await fetch('/api/download-db', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Download failed');
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `schoolsphere_backup_${new Date().toISOString().split('T')[0]}.db`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        setError('Download failed: ' + err.message);
      }
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="👤 Profile & Settings" size="lg">
      <div className="space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-2 rounded-md text-sm">
            ❌ {error}
          </div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-2 rounded-md text-sm">
            {success}
          </div>
        )}

        {/* ═══════════ SECTION 1: EDIT PROFILE ═══════════ */}
        <div className="bg-gray-50 p-4 rounded-lg border">
          <h4 className="font-bold text-gray-700 mb-3 flex items-center gap-2 text-lg">
            <i className="fas fa-user-edit text-blue-500"></i> Edit Profile
          </h4>

          <form onSubmit={handleProfileUpdate} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">First Name *</label>
                <input
                  className="w-full p-2.5 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="First Name"
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Last Name *</label>
                <input
                  className="w-full p-2.5 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="Last Name"
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  required
                />
              </div>
            </div>

            {/* Email — locked */}
            <div>
              <label className="block text-xs text-gray-500 mb-1">Email (login)</label>
              <div className="relative">
                <input
                  className="w-full p-2.5 border rounded-md bg-gray-100 text-gray-500 cursor-not-allowed pr-10"
                  value={user?.email || ''}
                  disabled
                  title="Email can only be changed by an administrator"
                />
                <i className="fas fa-lock absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">Phone</label>
              <input
                className="w-full p-2.5 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="+92-300-1234567"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">Address</label>
              <input
                className="w-full p-2.5 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="Address"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
            </div>

            {/* CNIC — locked */}
            <div>
              <label className="block text-xs text-gray-500 mb-1">CNIC / ID Number</label>
              <div className="relative">
                <input
                  className="w-full p-2.5 border rounded-md bg-gray-100 text-gray-500 cursor-not-allowed pr-10"
                  value={user?.cnic || '—'}
                  disabled
                  title="CNIC can only be changed by an administrator"
                />
                <i className="fas fa-lock absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              </div>
            </div>

            <p className="text-xs text-gray-400 italic flex items-start gap-1">
              <i className="fas fa-info-circle mt-0.5"></i>
              <span>
                Email and CNIC are managed identities — contact an
                administrator to change them.
              </span>
            </p>

            {isAdmin && (
              <p className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                <i className="fas fa-user-shield mr-1"></i>
                As an <strong>admin</strong>, use <strong>User Management</strong> to
                edit anyone's email, CNIC, or role.
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="bg-blue-600 text-white px-6 py-2.5 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {loading ? '⏳ Updating...' : '💾 Update Profile'}
            </button>
          </form>
        </div>

        {/* ═══════════ SECTION 2: CHANGE PASSWORD ═══════════ */}
        <div className="bg-gray-50 p-4 rounded-lg border">
          <h4 className="font-bold text-gray-700 mb-3 flex items-center gap-2 text-lg">
            <i className="fas fa-key text-yellow-500"></i> Change Password
          </h4>

          <form onSubmit={handlePasswordChange} className="space-y-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Current Password *</label>
              <input
                className="w-full p-2.5 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                type="password"
                placeholder="Current Password"
                value={passwordData.old_password}
                onChange={(e) => setPasswordData({ ...passwordData, old_password: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">New Password *</label>
                <input
                  className="w-full p-2.5 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  type="password"
                  placeholder="Min 8 characters"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  required
                  minLength={8}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Confirm New Password *</label>
                <input
                  className="w-full p-2.5 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  type="password"
                  placeholder="Repeat new password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  required
                  minLength={8}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="bg-green-600 text-white px-6 py-2.5 rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {loading ? '⏳ Changing...' : '🔑 Change Password'}
            </button>
          </form>
        </div>

        {/* ═══════════ SECTION 3: DATABASE BACKUP (admin only) ═══════════ */}
        {isAdmin && (
          <div className="bg-gray-50 p-4 rounded-lg border">
            <h4 className="font-bold text-gray-700 mb-3 flex items-center gap-2 text-lg">
              <i className="fas fa-database text-purple-500"></i> Database Backup
            </h4>
            <button
              onClick={handleDownloadDB}
              className="bg-purple-600 text-white px-6 py-2.5 rounded-md hover:bg-purple-700 transition-colors flex items-center gap-2"
            >
              <i className="fas fa-download"></i> Download Database
            </button>
            <p className="text-xs text-gray-400 mt-2">
              <i className="fas fa-info-circle mr-1"></i>
              Download the entire SQLite database as a backup file.
              This endpoint is admin-only. For production, prefer the
              scheduled backups in <code className="bg-gray-200 px-1 rounded">backend/backups/</code>.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}